import React from 'react';

export const SparkleStyles = () => (
  <style>{`
    @keyframes sparkle {
      0%, 100% { opacity: 0.15; transform: scale(0.6); }
      50% { opacity: 1; transform: scale(1.6); }
    }
    @keyframes taskExpand {
      from { opacity: 0; max-height: 0; transform: translateY(-8px); }
      to { opacity: 1; max-height: 1200px; transform: translateY(0); }
    }
    @keyframes taskCollapse {
      from { opacity: 1; max-height: 1200px; }
      to { opacity: 0; max-height: 0; }
    }
    .task-expand-enter { animation: taskExpand 0.4s cubic-bezier(0.16,1,0.3,1) forwards; }
    @keyframes borderPulse {
      0%, 100% { opacity: 0.7; }
      50% { opacity: 1; }
    }
    @keyframes glowBreath {
      0%, 100% { opacity: 0.6; }
      50% { opacity: 1; }
    }
    @keyframes slowTwinkle {
      0%, 100% { opacity: 0.52; }
      50% { opacity: 0.62; }
    }
    @keyframes subtleTwinkle {
      0%, 100% { opacity: 0.45; }
      50% { opacity: 0.55; }
    }
    @keyframes ctaPulse {
      0%, 100% { box-shadow: 0 0 18px rgba(34,211,238,0.4), 0 0 40px rgba(34,211,238,0.15), inset 0 0 12px rgba(34,211,238,0.1); }
      50% { box-shadow: 0 0 24px rgba(34,211,238,0.6), 0 0 50px rgba(34,211,238,0.25), inset 0 0 16px rgba(34,211,238,0.15); }
    }
    @keyframes fadeSlideIn {
      from { opacity: 0; transform: translateY(-12px); }
      to { opacity: 1; transform: translateY(0); }
    }

    /* ── Dashboard font ── */
    .dashboard-page, .dashboard-page * {
      font-family: "Inter", "SF Pro Display", "Manrope", "Plus Jakarta Sans", system-ui, sans-serif;
    }

    /* ── Multi-layer dashboard background ── */
    .dashboard-page {
      position: relative;
      min-height: 100vh;
      overflow-x: hidden;
      isolation: isolate;
      background: transparent;
      color: #F8FAFC;
    }

    /* Fog / mist layer - extra ambient glow for dashboard */
    .dashboard-page::before {
      content: "";
      position: fixed;
      inset: 0;
      pointer-events: none;
      z-index: -3;
      background:
        radial-gradient(circle at 12% 20%, rgba(59,130,246,0.08), transparent 22%),
        radial-gradient(circle at 78% 18%, rgba(168,85,247,0.06), transparent 18%),
        radial-gradient(circle at 75% 78%, rgba(245,158,11,0.06), transparent 16%),
        radial-gradient(circle at 30% 70%, rgba(56,189,248,0.05), transparent 16%);
      filter: blur(24px);
      opacity: 0.9;
    }

    /* Star layer 1 — dimmer, natural */
    .dashboard-stars-1 {
      position: fixed;
      inset: 0;
      pointer-events: none;
      z-index: -2;
      opacity: 0.52;
      background-image:
        radial-gradient(circle, rgba(255,255,255,0.18) 0px, rgba(255,255,255,0.18) 0.9px, transparent 1.6px),
        radial-gradient(circle, rgba(125,211,252,0.16) 0px, rgba(125,211,252,0.16) 1px, transparent 1.8px),
        radial-gradient(circle, rgba(196,181,253,0.14) 0px, rgba(196,181,253,0.14) 1px, transparent 1.7px);
      background-size: 180px 180px, 260px 260px, 320px 320px;
      background-position: 20px 40px, 100px 120px, 60px 200px;
      filter: blur(0.2px);
      animation: slowTwinkle 6s ease-in-out infinite;
    }

    /* Star layer 2 — brighter accents */
    .dashboard-stars-2 {
      position: fixed;
      inset: 0;
      pointer-events: none;
      z-index: -1;
      opacity: 0.34;
      background-image:
        radial-gradient(circle, rgba(255,255,255,0.12) 0px, rgba(255,255,255,0.12) 0.8px, transparent 1.4px),
        radial-gradient(circle, rgba(245,158,11,0.10) 0px, rgba(245,158,11,0.10) 1px, transparent 1.8px),
        radial-gradient(circle, rgba(255,255,255,0.10) 0px, rgba(255,255,255,0.10) 1.4px, transparent 2px);
      background-size: 140px 140px, 240px 240px, 360px 360px;
      background-position: 30px 10px, 90px 70px, 240px 40px;
    }

    /* Ambient glow */
    .dashboard-ambient-glow {
      position: fixed;
      inset: 0;
      pointer-events: none;
      z-index: -4;
      background:
        radial-gradient(circle at 20% 30%, rgba(99,102,241,0.08), transparent 20%),
        radial-gradient(circle at 75% 70%, rgba(245,158,11,0.07), transparent 18%),
        radial-gradient(circle at 55% 45%, rgba(56,189,248,0.05), transparent 18%);
      filter: blur(50px);
      opacity: 0.85;
    }

    /* ── Glass card system ── */
    .glass-card {
      position: relative;
      overflow: hidden;
      border-radius: 24px;
      background: linear-gradient(180deg, rgba(16,24,48,0.58) 0%, rgba(10,18,38,0.72) 100%);
      backdrop-filter: blur(18px);
      -webkit-backdrop-filter: blur(18px);
      border: 1px solid rgba(255,255,255,0.08);
      box-shadow: 0 12px 40px rgba(0,0,0,0.24), inset 0 1px 0 rgba(255,255,255,0.05), inset 0 -1px 0 rgba(255,255,255,0.02);
    }
    .glass-card::before {
      content: "";
      position: absolute;
      inset: 0;
      border-radius: inherit;
      pointer-events: none;
      z-index: 0;
      background: linear-gradient(180deg, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0.015) 28%, rgba(255,255,255,0.00) 100%);
    }
    .glass-card::after {
      content: "";
      position: absolute;
      inset: 0;
      border-radius: inherit;
      pointer-events: none;
      z-index: 0;
      background:
        radial-gradient(circle at 18% 16%, rgba(56,189,248,0.05), transparent 28%),
        radial-gradient(circle at 82% 78%, rgba(245,158,11,0.05), transparent 24%);
      opacity: 0.9;
    }
    .glass-card > * {
      position: relative;
      z-index: 1;
    }
    .glass-card-active {
      box-shadow: 0 0 0 1px rgba(255,255,255,0.03), 0 0 16px rgba(56,189,248,0.10), 0 12px 30px rgba(0,0,0,0.18);
    }

    /* ── Gold particle layer ── */
    .task-card-gold-particles {
      position: absolute;
      right: 0;
      bottom: 0;
      width: 42%;
      height: 46%;
      pointer-events: none;
      z-index: 0;
      background-image:
        radial-gradient(circle, rgba(245,158,11,0.18) 0px, rgba(245,158,11,0.18) 1px, transparent 1.7px),
        radial-gradient(circle, rgba(251,191,36,0.16) 0px, rgba(251,191,36,0.16) 1.3px, transparent 2px),
        radial-gradient(circle, rgba(255,220,120,0.12) 0px, rgba(255,220,120,0.12) 1.6px, transparent 2.3px);
      background-size: 80px 80px, 120px 120px, 160px 160px;
      background-position: 10px 20px, 40px 60px, 80px 30px;
      opacity: 0.85;
      filter: drop-shadow(0 0 6px rgba(245,158,11,0.12));
    }

    /* ── Text glass panel ── */
    .text-glass-panel {
      background: linear-gradient(180deg, rgba(255,255,255,0.03), rgba(255,255,255,0.01));
      border: 1px solid rgba(255,255,255,0.06);
      box-shadow: inset 0 1px 0 rgba(255,255,255,0.03);
      border-radius: 18px;
      padding: 16px 18px;
    }

    /* ── Star highlight layer — brighter LED dots ── */
    .dashboard-stars-highlight {
      position: fixed;
      inset: 0;
      pointer-events: none;
      z-index: -1;
      opacity: 0.45;
      background-image:
        radial-gradient(circle, rgba(255,255,255,0.30) 0px, rgba(255,255,255,0.30) 1.2px, transparent 2px),
        radial-gradient(circle, rgba(125,211,252,0.28) 0px, rgba(125,211,252,0.28) 1.4px, transparent 2.2px),
        radial-gradient(circle, rgba(245,158,11,0.22) 0px, rgba(245,158,11,0.22) 1.2px, transparent 2px),
        radial-gradient(circle, rgba(196,181,253,0.22) 0px, rgba(196,181,253,0.22) 1.2px, transparent 2px);
      background-size: 220px 220px, 300px 300px, 360px 360px, 420px 420px;
      background-position: 40px 30px, 140px 80px, 260px 160px, 320px 60px;
      filter: drop-shadow(0 0 8px rgba(255,255,255,0.16));
      animation: subtleTwinkle 7s ease-in-out infinite;
    }

    /* ═══════ TASK CARD SYSTEM ═══════ */
    .card-task {
      position: relative;
      border-radius: 22px;
      overflow: hidden;
      background: linear-gradient(160deg, rgba(15,23,42,0.82), rgba(10,18,38,0.92));
      backdrop-filter: blur(16px);
      -webkit-backdrop-filter: blur(16px);
      border: 1px solid transparent;
      box-shadow: 0 0 0 1px rgba(255,255,255,0.04), 0 10px 40px rgba(0,0,0,0.6);
    }
    .card-task-border {
      position: absolute;
      inset: 0;
      border-radius: 22px;
      padding: 1px;
      background: linear-gradient(120deg, rgba(120,80,20,0.2), rgba(255,200,80,0.9), rgba(255,220,120,1), rgba(255,200,80,0.9), rgba(120,80,20,0.2));
      -webkit-mask: linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0);
      -webkit-mask-composite: xor;
      mask-composite: exclude;
      pointer-events: none;
      z-index: 20;
      box-shadow: 0 0 10px rgba(255,200,80,0.7), 0 0 25px rgba(255,200,80,0.5), 0 0 60px rgba(255,200,80,0.35);
    }
    .card-task-glow {
      position: absolute;
      inset: 0;
      background: radial-gradient(circle at 15% 10%, rgba(255,210,120,0.35), transparent 40%), radial-gradient(circle at 85% 90%, rgba(255,180,60,0.45), transparent 45%);
      filter: blur(45px);
      opacity: 0.9;
      pointer-events: none;
      z-index: 1;
    }
    .card-task-stars {
      position: absolute;
      inset: 0;
      pointer-events: none;
      z-index: 2;
      background:
        radial-gradient(circle, rgba(255,255,255,0.85) 0px, rgba(255,255,255,0.85) 0.6px, transparent 1.2px) 20% 20% / 140px 140px,
        radial-gradient(circle, rgba(255,215,120,0.85) 0px, rgba(255,215,120,0.85) 0.7px, transparent 1.4px) 80% 85% / 120px 120px,
        radial-gradient(circle, rgba(255,255,255,0.55) 0px, rgba(255,255,255,0.55) 0.5px, transparent 1px) 60% 40% / 200px 200px;
      filter: drop-shadow(0 0 3px rgba(255,220,120,0.6));
      opacity: 0.6;
    }
    .card-task-lightline {
      position: absolute;
      bottom: 12px;
      left: 12%;
      right: 12%;
      height: 2px;
      z-index: 3;
      background: linear-gradient(90deg, transparent, rgba(255,200,80,1), transparent);
      box-shadow: 0 0 12px rgba(255,200,80,1), 0 0 30px rgba(255,200,80,0.6);
    }
    .section-how {
      margin-top: 18px;
      padding: 16px;
      border-radius: 14px;
      background: rgba(255,200,80,0.05);
      border: 1px solid rgba(255,200,80,0.4);
      box-shadow: inset 0 0 25px rgba(255,200,80,0.08), 0 0 15px rgba(255,200,80,0.15);
    }
    .section-how-title {
      display: flex;
      align-items: center;
      gap: 8px;
      color: #FCD34D;
      font-weight: 600;
    }
    .section-how-title svg {
      filter: drop-shadow(0 0 8px rgba(255,200,80,1)) drop-shadow(0 0 20px rgba(255,200,80,0.7));
    }
    .btn-task-done {
      background: rgba(56,189,248,0.12);
      color: #67E8F9;
      border: 1px solid rgba(56,189,248,0.6);
      box-shadow: 0 0 15px rgba(56,189,248,0.7), inset 0 0 10px rgba(56,189,248,0.25);
    }
    .btn-task-done.btn-active {
      background: rgba(56,189,248,0.30);
      border-color: rgba(56,189,248,0.9);
      box-shadow: 0 0 20px rgba(56,189,248,0.9), 0 0 40px rgba(56,189,248,0.4), inset 0 0 15px rgba(56,189,248,0.35);
      transform: scale(1.03);
    }
    .btn-task-partial {
      background: rgba(168,85,247,0.12);
      color: #C4B5FD;
      border: 1px solid rgba(168,85,247,0.6);
      box-shadow: 0 0 15px rgba(168,85,247,0.7), inset 0 0 10px rgba(168,85,247,0.25);
    }
    .btn-task-partial.btn-active {
      background: rgba(168,85,247,0.30);
      border-color: rgba(168,85,247,0.9);
      box-shadow: 0 0 20px rgba(168,85,247,0.9), 0 0 40px rgba(168,85,247,0.4), inset 0 0 15px rgba(168,85,247,0.35);
      transform: scale(1.03);
    }
    .btn-task-skip {
      background: rgba(248,113,113,0.1);
      color: #FCA5A5;
      border: 1px solid rgba(248,113,113,0.5);
    }
    .btn-task-skip.btn-active {
      background: rgba(248,113,113,0.25);
      border-color: rgba(248,113,113,0.85);
      box-shadow: 0 0 20px rgba(248,113,113,0.7), 0 0 40px rgba(248,113,113,0.3), inset 0 0 15px rgba(248,113,113,0.25);
      transform: scale(1.03);
    }

    /* ═══════ PLAN PANEL SYSTEM ═══════ */
    .panel-plan {
      position: relative;
      overflow: hidden;
      border-radius: 24px;
      padding: 22px 20px;
      background: linear-gradient(180deg, rgba(16,24,48,0.60), rgba(10,18,38,0.78));
      backdrop-filter: blur(18px);
      -webkit-backdrop-filter: blur(18px);
      border: 1px solid rgba(255,255,255,0.08);
      box-shadow: 0 12px 40px rgba(0,0,0,0.24), inset 0 1px 0 rgba(255,255,255,0.05), inset 0 -1px 0 rgba(255,255,255,0.02);
    }
    .panel-plan-ambient {
      position: absolute;
      inset: 0;
      pointer-events: none;
      border-radius: inherit;
      background:
        radial-gradient(circle at 20% 20%, rgba(56,189,248,0.05), transparent 26%),
        radial-gradient(circle at 85% 15%, rgba(168,85,247,0.04), transparent 18%),
        radial-gradient(circle at 80% 90%, rgba(245,158,11,0.05), transparent 24%);
    }
    .plan-module {
      position: relative;
      display: flex;
      align-items: center;
      gap: 14px;
      min-height: 72px;
      padding: 14px 16px;
      border-radius: 18px;
      background: linear-gradient(180deg, rgba(18,28,52,0.60), rgba(8,16,34,0.74));
      border: 1px solid rgba(255,255,255,0.07);
      box-shadow: 0 8px 24px rgba(0,0,0,0.16), inset 0 1px 0 rgba(255,255,255,0.03);
      transition: all 0.18s ease;
      cursor: pointer;
    }
    .plan-module:hover { transform: translateY(-1px); }
    .plan-module--anxiety {
      background: linear-gradient(180deg, rgba(42,32,14,0.42), rgba(20,20,30,0.72));
      border: 1px solid rgba(245,158,11,0.34);
      box-shadow: 0 0 0 1px rgba(255,255,255,0.02), 0 0 22px rgba(245,158,11,0.16), inset 0 0 28px rgba(245,158,11,0.07);
    }
    .plan-module--sleep {
      background: linear-gradient(180deg, rgba(12,36,54,0.42), rgba(14,20,34,0.74));
      border: 1px solid rgba(56,189,248,0.34);
      box-shadow: 0 0 0 1px rgba(255,255,255,0.02), 0 0 22px rgba(56,189,248,0.16), inset 0 0 28px rgba(56,189,248,0.07);
    }
    .plan-module--mood {
      background: linear-gradient(180deg, rgba(28,18,52,0.42), rgba(16,12,34,0.74));
      border: 1px solid rgba(168,85,247,0.34);
      box-shadow: 0 0 0 1px rgba(255,255,255,0.02), 0 0 22px rgba(168,85,247,0.16), inset 0 0 28px rgba(168,85,247,0.07);
    }
    .plan-module--energy {
      background: linear-gradient(180deg, rgba(42,32,14,0.42), rgba(20,20,30,0.72));
      border: 1px solid rgba(245,158,11,0.34);
      box-shadow: 0 0 0 1px rgba(255,255,255,0.02), 0 0 22px rgba(245,158,11,0.16), inset 0 0 28px rgba(245,158,11,0.07);
    }
    .plan-module--locked {
      background: linear-gradient(180deg, rgba(18,24,40,0.52), rgba(10,16,28,0.78));
      border: 1px solid rgba(255,255,255,0.05);
      opacity: 0.88;
    }
    .plan-icon { width: 42px; height: 42px; min-width: 42px; border-radius: 999px; display: flex; align-items: center; justify-content: center; }
    .plan-icon--anxiety {
      background: rgba(245,158,11,0.12);
      border: 1px solid rgba(245,158,11,0.24);
      box-shadow: 0 0 14px rgba(245,158,11,0.26), inset 0 0 12px rgba(245,158,11,0.10);
    }
    .plan-icon--anxiety svg { color: #FCD34D; filter: drop-shadow(0 0 8px rgba(245,158,11,0.9)) drop-shadow(0 0 16px rgba(245,158,11,0.45)); }
    .plan-icon--sleep {
      background: rgba(56,189,248,0.12);
      border: 1px solid rgba(56,189,248,0.24);
      box-shadow: 0 0 14px rgba(56,189,248,0.24), inset 0 0 12px rgba(56,189,248,0.10);
    }
    .plan-icon--sleep svg { color: #67E8F9; filter: drop-shadow(0 0 8px rgba(56,189,248,0.9)) drop-shadow(0 0 16px rgba(56,189,248,0.45)); }
    .plan-icon--mood {
      background: rgba(168,85,247,0.12);
      border: 1px solid rgba(168,85,247,0.24);
      box-shadow: 0 0 14px rgba(168,85,247,0.24), inset 0 0 12px rgba(168,85,247,0.10);
    }
    .plan-icon--mood svg { color: #C084FC; filter: drop-shadow(0 0 8px rgba(168,85,247,0.9)) drop-shadow(0 0 16px rgba(168,85,247,0.45)); }
    .plan-icon--energy {
      background: rgba(245,158,11,0.12);
      border: 1px solid rgba(245,158,11,0.24);
      box-shadow: 0 0 14px rgba(245,158,11,0.26), inset 0 0 12px rgba(245,158,11,0.10);
    }
    .plan-icon--energy svg { color: #FCD34D; filter: drop-shadow(0 0 8px rgba(245,158,11,0.9)) drop-shadow(0 0 16px rgba(245,158,11,0.45)); }
    .plan-icon--locked {
      background: rgba(148,163,184,0.08);
      border: 1px solid rgba(148,163,184,0.14);
      box-shadow: 0 0 10px rgba(148,163,184,0.08), inset 0 0 8px rgba(255,255,255,0.02);
    }
    .plan-icon--locked svg { color: #94A3B8; filter: drop-shadow(0 0 4px rgba(148,163,184,0.18)); }
    .plan-icon--cognitive {
      background: radial-gradient(circle at 50% 50%, rgba(20,184,166,0.25), rgba(13,148,136,0.12));
      border: 1px solid rgba(20,184,166,0.35);
      box-shadow: 0 0 12px rgba(20,184,166,0.15), inset 0 0 12px rgba(20,184,166,0.08);
    }
    .plan-icon--cognitive svg { color: #2DD4BF; filter: drop-shadow(0 0 8px rgba(20,184,166,0.9)) drop-shadow(0 0 16px rgba(20,184,166,0.45)); }
    .plan-icon--cardiovascular {
      background: radial-gradient(circle at 50% 50%, rgba(244,63,94,0.25), rgba(225,29,72,0.12));
      border: 1px solid rgba(244,63,94,0.35);
      box-shadow: 0 0 12px rgba(244,63,94,0.15), inset 0 0 12px rgba(244,63,94,0.08);
    }
    .plan-icon--cardiovascular svg { color: #FB7185; filter: drop-shadow(0 0 8px rgba(244,63,94,0.9)) drop-shadow(0 0 16px rgba(244,63,94,0.45)); }
  `}</style>
);
